import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * The <source> elements are authored with `data-video-source` rather than `src` (see
 * snippets/background-media.liquid), so nothing is fetched until this component promotes them.
 *
 * 2026-08-23 (speed): promotion now waits until the component is at or near the viewport instead
 * of happening the moment the module runs. The homepage carries eleven of these - a five-slide
 * hero slideshow plus six banner strips - and every one of them used to begin downloading during
 * the initial page load, all competing for the same connections. Each component already renders
 * the video's own preview image behind the <video>, so a background that has not been promoted
 * yet is visually identical to one that has: the still frame is on screen either way.
 *
 * Anything already in view at load (the hero) promotes immediately, so the top of the page is
 * unchanged. Everything below the fold starts as the shopper approaches it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** Distance from the viewport at which a background video starts loading. */
  static ROOT_MARGIN = '400px 0px';

  connectedCallback() {
    super.connectedCallback();

    // No IntersectionObserver (very old browser): keep the original eager behaviour.
    if (typeof IntersectionObserver === 'undefined') {
      this.#load();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#load();
      },
      { rootMargin: VideoBackgroundComponent.ROOT_MARGIN }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.observer?.disconnect();
    this.observer = null;
  }

  #load() {
    if (this.loaded) return;
    this.loaded = true;
    this.observer?.disconnect();
    this.observer = null;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
